import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { LoadingState } from "@repo/app-types/client/client";
import type { Assessment } from "@repo/app-types/entities/assessment";
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInsertClient,
} from "@repo/app-types/entities/customField";
import type { Selector } from "react-redux";
import { getAxiosInstanceWithAuth } from "../../api/axiosConfig.js";
import type { RootState } from "../../app/store.js";
import { createProject } from "../projects/projectsSlice.js";

export const assessmentsAdapter = createEntityAdapter<Assessment>();

const initialState = assessmentsAdapter.getInitialState<{
  status: LoadingState;
  error: string | null;
}>({
  status: LoadingState.LOADING,
  error: null,
});

export const updateAssessment = createAsyncThunk<
  Assessment,
  {
    assessmentId: string;
    changes: {
      custom_field_definitions: (
        | CustomFieldDefinition
        | CustomFieldDefinitionInsertClient
      )[];
    };
  },
  { rejectValue: string }
>(
  "assessments/updateAssessment",
  async ({ assessmentId, changes }, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.post(
        `/assessments/${assessmentId}`,
        changes,
      );
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchAssessmentById = createAsyncThunk(
  "assessments/fetchAssessmentById",
  async (assessmentId: string, thunkAPI) => {
    try {
      const axios = await getAxiosInstanceWithAuth();
      const response = await axios.get(`/assessments/${assessmentId}`);
      return response.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

const assessmentsSlice = createSlice({
  name: "assessments",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(updateAssessment.pending, (state) => {
        state.status = LoadingState.LOADING;
      })
      .addCase(updateAssessment.fulfilled, (state, action) => {
        state.status = LoadingState.LOADED;
        assessmentsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: action.payload,
        });
      })
      .addCase(updateAssessment.rejected, (state, action) => {
        state.status = LoadingState.ERROR;
        state.error = action.payload || null;
      })
      .addCase(createProject.fulfilled, (state, action) => {
        if (action.payload.assessment) {
          assessmentsAdapter.addOne(state, action.payload.assessment);
        }
      })
      .addCase(fetchAssessmentById.pending, (state) => {
        state.status = LoadingState.LOADING;
      })
      .addCase(fetchAssessmentById.fulfilled, (state, action) => {
        state.status = LoadingState.LOADED;
        assessmentsAdapter.addOne(state, action.payload);
      })
      .addCase(fetchAssessmentById.rejected, (state, action) => {
        state.status = LoadingState.ERROR;
        state.error = action.error.message || null;
      });
  },
});

export default assessmentsSlice.reducer;

export const selectAssessmentById = (
  id: string | undefined | null,
): Selector<RootState, Assessment | undefined> =>
  createSelector(
    [(state: RootState) => state.assessments],
    (assessmentsState) =>
      id
        ? assessmentsAdapter.getSelectors().selectById(assessmentsState, id)
        : undefined,
  );
